import {
  TopBottom,
  IBallFinal,
  BallFinalKinds,
  IApiTaggingPlayers,
  IApiCatcherThrowTag,
  IDataGridRowStatus,
  IApiPitchTag,
  baseballLogicService,
  IPlayMetadata,
  Dexterity,
  constants,
  IApiLineupPlayer,
  IApiPlayerExtended,
} from '@trackman/baseball-shared-components';
import moment from 'moment';
import { emptyGuid, undefinedValue, emptyPlayer } from 'constants/Constants';
import { IChangedValue } from 'interfaces/merge/IChangedValue';
import { EditRowDataKey } from 'enums/EditRowDataKey';
import { DataKey } from '../../enums/DataKey';
import { PlayRowMetrics } from './PlayRowMetrics';
import { IMergedPlayWithBallFinal } from '../../interfaces/sessionVerification/plays/IPlayWithBallFinal';
import { IEditRowInning } from '../../interfaces/sessionVerification/plays/IEditRowInning';
import { IEditRowPlayer, IEditRowCatcher, IEditRowBatter } from '../../interfaces/sessionVerification/plays/IEditRowPlayer';
import { IApiTeam } from '../../interfaces/team/IApiTeam';
import { IBoxScoreRequestPlay } from '../../interfaces/boxScore/IBoxScoreRequest';

interface IFieldingSequence {
  fielder1: IApiPlayerExtended;
  fielder2: IApiPlayerExtended;
  fielder3: IApiPlayerExtended;
}

type IBaseRunners = {
  firstBase: IApiPlayerExtended;
  secondBase: IApiPlayerExtended;
  thirdBase: IApiPlayerExtended;
};

type IPlayMetadataWithPlayerTracking = IPlayMetadata & { baseRunners: IBaseRunners; fieldingSequence: IFieldingSequence };

export class PlayRowProps extends PlayRowMetrics {
  constructor(playData: IMergedPlayWithBallFinal, homeTeam: IApiTeam, awayTeam: IApiTeam) {
    const { play, ballFinals, isChanged, isCreated, isMarkedForDeletion, changedValues, isBallTrackingChanged, isPlayerTrackingChanged } =
      playData;

    super(play, ballFinals);
    this.id = play.id;
    this.playId = play.playId;
    this.homeTeam = homeTeam;
    this.awayTeam = awayTeam;
    this.balls = play.gameState.balls;
    this.batter = {
      id: play.players.batter.id,
      nameRef: play.players.batter.nameRef,
      name: play.players.batter.nameRef,
      position: play.players.batter.position,
      foreignIds: play.players.batter.foreignIds,
      isPlaceholder: play.players.batter.isPlaceholder,
      teamName: baseballLogicService.isHomeTeamBatting(play.gameState.topBottom === TopBottom.Top) ? homeTeam.name : awayTeam.name,
      battingHandedness: play.players.batter.battingHandedness,
      height: play.players.batter.height,
      strikeZoneBottom: play.players.batter.strikeZoneBottom,
      strikeZoneTop: play.players.batter.strikeZoneTop,
      jerseyNumber: play.players.batter.jerseyNumber,
    };
    this.batterDexterity = play.players.batter.battingHandedness;
    this.hitType = play.hitTag.hitType;
    this.inning = {
      inningNumber: play.gameState.inning,
      isTopInning: play.gameState.topBottom === TopBottom.Top,
    };
    this.korbb = play.kOrBB;
    this.note = play.note;
    this.outs = play.gameState.outs;
    this.outsOnPlay = play.playResult.outs;
    this.pitchCall = play.pitchTag.pitchCall;
    this.playSequenceNumber = play.taggerBehavior.playSequenceNumber;
    this.sequenceNumber = play.taggerBehavior.sequenceNumber;
    this.pitcher = {
      id: play.players.pitcher.id,
      nameRef: play.players.pitcher.nameRef,
      name: play.players.pitcher.nameRef,
      position: play.players.pitcher.position,
      foreignIds: play.players.pitcher.foreignIds,
      isPlaceholder: play.players.pitcher.isPlaceholder,
      teamName: baseballLogicService.isHomeTeamBatting(play.gameState.topBottom === TopBottom.Top) ? awayTeam.name : homeTeam.name,
      height: play.players.pitcher.height,
      strikeZoneBottom: play.players.pitcher.strikeZoneBottom,
      strikeZoneTop: play.players.pitcher.strikeZoneTop,
      jerseyNumber: play.players.pitcher.jerseyNumber,
    };
    this.catcher = {
      id: play.players.catcher.id,
      nameRef: play.players.catcher.nameRef,
      name: play.players.catcher.nameRef,
      position: play.players.catcher.position,
      foreignIds: play.players.catcher.foreignIds,
      pitchingHandedness: play.players.catcher.pitchingHandedness,
      isPlaceholder: play.players.catcher.isPlaceholder,
      teamName: baseballLogicService.isHomeTeamBatting(play.gameState.topBottom === TopBottom.Top) ? awayTeam.name : homeTeam.name,
      height: play.players.catcher.height,
      strikeZoneBottom: play.players.catcher.strikeZoneBottom,
      strikeZoneTop: play.players.catcher.strikeZoneTop,
      jerseyNumber: play.players.catcher.jerseyNumber,
    };
    this.pitcherDexterity = play.players.pitcher.pitchingHandedness;
    this.strikes = play.gameState.strikes;
    this.taggedPitchType = play.pitchTag.pitchType;
    this.isChanged = isChanged;
    this.isBallTrackingChanged = isBallTrackingChanged;
    this.isPlayerTrackingChanged = isPlayerTrackingChanged;
    this.runsScored = play.playResult.runs;
    this.isAddedManually = play.taggerBehavior.manuallyAddedByTagger;
    this.isWarmup = play.taggerBehavior.isWarmup;
    this.isDeleted = play.taggerBehavior.isDeleted;
    this.homeTeamScore = play.gameState.homeTeamScore;
    this.awayTeamScore = play.gameState.awayTeamScore;
    this.playResult = play.playResult.result;
    this.pitchBallFinalTrackId = play.pitchTag.ballFinalTrackId;
    this.hitBallFinalTrackId = play.hitTag?.ballFinalTrackId;
    this.catcherThrowBallFinalTrackId = play.catcherThrowTag?.ballFinalTrackId;
    this.pitchOfPA = play.taggerBehavior.pitchOfPA;
    this.pAofInning = play.taggerBehavior.pAofInning;
    this.status = {
      errors: [],
      status: '',
    };
    this.catcherThrowTag = play.catcherThrowTag;
    this.trackStartTimeUtc = play.trackStartTimeUtc;
    this.trackStartTimeLocal = play.trackStartTimeLocal;
    this.createdInStadiumUtc = play.createdInStadiumUtc;
    this.createdInStadiumLocal = play.createdInStadiumLocal;
    this.updatedInStadiumUtc = play.updatedInStadiumUtc;
    this.updatedInStadiumLocal = play.updatedInStadiumLocal;
    this.sessionId = play.sessionId;
    this.isCreated = isCreated;
    this.isMarkedForDeletion = isMarkedForDeletion;
    this.displayIndex = -1;
    this.mergeBallFinalData(ballFinals);
    this.changedValues = changedValues;
    this.homeTeamLineup =
      (baseballLogicService.isHomeTeamBatting(play.gameState.topBottom === TopBottom.Top)
        ? play.playLineup?.battingTeam
        : play.playLineup?.pitchingTeam) || [];
    this.awayTeamLineup =
      (baseballLogicService.isHomeTeamBatting(play.gameState.topBottom === TopBottom.Top)
        ? play.playLineup?.pitchingTeam
        : play.playLineup?.battingTeam) || [];
    this.firstBase = play.baseRunners?.firstBase;
    this.secondBase = play.baseRunners?.secondBase;
    this.thirdBase = play.baseRunners?.thirdBase;
    this.firstBaseman = play.players.firstBaseman;
    this.secondBaseman = play.players.secondBaseman;
    this.thirdBaseman = play.players.thirdBaseman;
    this.shortStopper = play.players.shortStopper;
    this.leftFielder = play.players.leftFielder;
    this.centerFielder = play.players.centerFielder;
    this.rightFielder = play.players.rightFielder;
    this.fieldingSequence = play.fieldingSequence;
  }

  private mergeBallFinalData(ballFinals: IBallFinal[]) {
    ballFinals.forEach((bf) => {
      switch (bf.kind) {
        case BallFinalKinds.Pitch:
          if (this.pitchBallFinalTrackId !== bf.trackId) {
            this.pitchBallFinalTrackId = bf.trackId;
            this.isChanged = true;
            this.isBallTrackingChanged = true;
          }
          if (
            !this.trackStartTimeUtc ||
            moment.utc(this.trackStartTimeUtc).format(constants.defaultDateTimeFormat) !==
              moment.utc(bf.trackStartTime).format(constants.defaultDateTimeFormat)
          ) {
            this.trackStartTimeUtc = moment(bf.trackStartTime).format(constants.defaultDateTimeFormat);
            this.isChanged = true;
            this.isBallTrackingChanged = true;
          }
          break;
        case BallFinalKinds.Hit:
          if (this.hitBallFinalTrackId !== bf.trackId) {
            this.hitBallFinalTrackId = bf.trackId;
            this.isChanged = true;
            this.isBallTrackingChanged = true;
          }
          break;
        case BallFinalKinds.CatcherThrow:
          if (this.catcherThrowBallFinalTrackId !== bf.trackId) {
            this.catcherThrowBallFinalTrackId = bf.trackId;
            this.isChanged = true;
            this.isBallTrackingChanged = true;
          }
          break;
      }
    });
  }

  toApiPlay(): IMergedPlayWithBallFinal {
    const play = {
      ballFinals: [],
      isChanged: this.isChanged,
      isBallTrackingChanged: this.isBallTrackingChanged,
      isPlayerTrackingChanged: this.isPlayerTrackingChanged,
      play: {
        id: this.id,
        sessionId: this.sessionId,
        playId: this.playId,
        taggerBehavior: {
          manuallyAddedByTagger: this.isAddedManually,
          isWarmup: this.isWarmup,
          playSequenceNumber: this.playSequenceNumber,
          sequenceNumber: this.sequenceNumber,
          isDeleted: this.isDeleted,
          pitchOfPA: this.pitchOfPA,
          pAofInning: this.pAofInning,
        },
        players: this.rowToApiTaggingPlayersMap(),
        gameState: {
          homeTeamScore: this.homeTeamScore,
          awayTeamScore: this.awayTeamScore,
          isHomeTeamBatting: !this.inning.isTopInning,
          topBottom: this.inning.isTopInning ? TopBottom.Top : TopBottom.Bottom,
          balls: this.balls,
          strikes: this.strikes,
          outs: this.outs,
          inning: this.inning.inningNumber,
        },
        pitchTag: {
          ballFinalTrackId: this.pitchBallFinalTrackId,
          pitchCall: this.pitchCall,
          pitchType: this.taggedPitchType,
          pitcherSet: undefinedValue, // TODO: we don't have it in taggingApp right now
        } as IApiPitchTag,
        hitTag: {
          hitType: this.hitType,
          ballFinalTrackId: this.hitBallFinalTrackId,
        },
        catcherThrowTag: this.reviewRowToApiCatcherThrowTagMap(),
        playResult: {
          outs: this.outsOnPlay,
          runs: this.runsScored,
          result: this.playResult,
        },
        note: this.note,
        kOrBB: this.korbb,
        pitchingTeam: baseballLogicService.isHomeTeamBatting(this.inning.isTopInning) ? this.awayTeam : this.homeTeam,
        battingTeam: baseballLogicService.isHomeTeamBatting(this.inning.isTopInning) ? this.homeTeam : this.awayTeam,
        trackStartTimeUtc: this.trackStartTimeUtc,
        trackStartTimeLocal: this.trackStartTimeLocal,
        createdInStadiumUtc: this.createdInStadiumUtc,
        createdInStadiumLocal: this.createdInStadiumLocal,
        updatedInStadiumUtc: this.updatedInStadiumUtc,
        updatedInStadiumLocal: this.updatedInStadiumLocal,
        playLineup: {
          pitchingTeam: baseballLogicService.isHomeTeamBatting(this.inning.isTopInning) ? this.awayTeamLineup : this.homeTeamLineup,
          battingTeam: baseballLogicService.isHomeTeamBatting(this.inning.isTopInning) ? this.homeTeamLineup : this.awayTeamLineup,
        },
        fieldingSequence: this.fieldingSequence,
        baseRunners: {
          firstBase: this.firstBase,
          secondBase: this.secondBase,
          thirdBase: this.thirdBase,
        },
      } as IPlayMetadataWithPlayerTracking,
      isMarkedForDeletion: this.isDeleted,
      isCreated: false,
      changedValues: this.changedValues,
    } as IMergedPlayWithBallFinal;
    return play;
  }

  toBoxScorePlay(): IBoxScoreRequestPlay {
    return {
      id: this.id,
      gameState: {
        inning: this.inning.inningNumber,
        topBottom: this.inning.isTopInning ? TopBottom.Top : TopBottom.Bottom,
        strikes: this.strikes,
        balls: this.balls,
        outs: this.outs,
        isHomeTeamBatting: baseballLogicService.isHomeTeamBatting(this.inning.isTopInning),
      },
      battingTeam: baseballLogicService.isHomeTeamBatting(this.inning.isTopInning) ? { ...this.homeTeam } : { ...this.awayTeam },
      pitchingTeam: baseballLogicService.isHomeTeamBatting(this.inning.isTopInning) ? { ...this.awayTeam } : { ...this.homeTeam },
      kOrBB: this.korbb,
      playId: this.playId!,
      pitchTag: {
        pitchCall: this.pitchCall,
      },
      playResult: {
        outs: this.outsOnPlay,
        result: this.playResult!,
      },
      players: {
        batter: { id: this.batter.id, nameRef: this.batter.nameRef },
        pitcher: { id: this.pitcher.id, nameRef: this.pitcher.nameRef },
        catcher: { id: this.catcher.id, nameRef: this.catcher.nameRef },
      },
      sessionId: this.sessionId,
      taggerBehavior: {
        playSequenceNumber: this.playSequenceNumber!,
        sequenceNumber: this.sequenceNumber,
      },
    };
  }

  private rowToApiTaggingPlayersMap() {
    const players = {
      pitcher: {
        id: this.pitcher.id ? this.pitcher.id : emptyGuid,
        nameRef: this.pitcher.nameRef,
        position: this.pitcher.position,
        pitchingHandedness: this.pitcherDexterity,
        foreignIds: this.pitcher.foreignIds,
      },
      batter: {
        id: this.batter.id ? this.batter.id : emptyGuid,
        nameRef: this.batter.nameRef,
        position: this.batter.position,
        battingHandedness: this.batterDexterity,
        foreignIds: this.batter.foreignIds,
      },
      catcher: {
        id: this.catcher.id ? this.catcher.id : emptyGuid,
        nameRef: this.catcher.nameRef,
        position: this.catcher.position,
        foreignIds: this.catcher.foreignIds,
        pitchingHandedness: this.catcher.pitchingHandedness,
      },
      firstBaseman: this.firstBaseman,
      secondBaseman: this.secondBaseman,
      thirdBaseman: this.thirdBaseman,
      centerFielder: this.centerFielder,
      leftFielder: this.leftFielder,
      rightFielder: this.rightFielder,
      shortStopper: this.shortStopper,
    } as IApiTaggingPlayers;
    return players;
  }

  private reviewRowToApiCatcherThrowTagMap() {
    return {
      ballFinalTrackId: this.catcherThrowBallFinalTrackId,
      thrownPosition: this.catcherThrowTag ? this.catcherThrowTag.thrownPosition : undefinedValue,
      fielder: this.catcherThrowTag ? this.catcherThrowTag.fielder : { ...emptyPlayer },
    };
  }

  // Api play props
  id: string;

  playId?: string;

  note: string;

  playSequenceNumber?: number;

  sequenceNumber: number;

  time?: string;

  pitcher: IEditRowPlayer;

  catcher: IEditRowCatcher;

  pitcherDexterity: Dexterity | 'R' | 'L' | 'B';

  batter: IEditRowBatter;

  batterDexterity: Dexterity | 'R' | 'L' | 'B';

  inning: IEditRowInning;

  balls: number;

  strikes: number;

  outs: number;

  taggedPitchType: string;

  pitchCall: string;

  korbb: string;

  hitType: string;

  playResult?: string;

  outsOnPlay: number;

  runsScored: number;

  homeTeamScore: number;

  awayTeamScore: number;

  pitchBallFinalTrackId?: string;

  hitBallFinalTrackId?: string;

  catcherThrowBallFinalTrackId?: string;

  pAofInning?: number;

  pitchOfPA?: number;

  catcherThrowTag: IApiCatcherThrowTag;

  trackStartTimeLocal: string | null | undefined;

  trackStartTimeUtc: string | undefined | null;

  isAddedManually: boolean;

  isWarmup: boolean;

  isDeleted: boolean;

  // Control properties
  status: IDataGridRowStatus;

  displayIndex: number;

  isChanged: boolean;

  isPlayerTrackingChanged: boolean;

  isBallTrackingChanged: boolean;

  isCreated: boolean;

  isMarkedForDeletion: boolean;

  ignoredColumns: (DataKey | EditRowDataKey | string)[] = [];

  // [key: string]: any;

  changedValues: IChangedValue[];

  // non-display properties
  createdInStadiumUtc: string;

  createdInStadiumLocal: string;

  updatedInStadiumUtc: string;

  updatedInStadiumLocal: string;

  sessionId: string;

  homeTeam: IApiTeam;

  awayTeam: IApiTeam;

  homeTeamLineup: IApiLineupPlayer[];

  awayTeamLineup: IApiLineupPlayer[];

  firstBase: IApiPlayerExtended;

  secondBase: IApiPlayerExtended;

  thirdBase: IApiPlayerExtended;

  firstBaseman: IApiPlayerExtended;

  secondBaseman: IApiPlayerExtended;

  thirdBaseman: IApiPlayerExtended;

  shortStopper: IApiPlayerExtended;

  leftFielder: IApiPlayerExtended;

  centerFielder: IApiPlayerExtended;

  rightFielder: IApiPlayerExtended;

  fieldingSequence: IFieldingSequence;
}
